import { useEffect } from "react";
import { motion } from "framer-motion";

interface NotFoundPageProps {
  currentPath?: string;
}

export function NotFoundPage({ currentPath = "" }: NotFoundPageProps) {
  useEffect(() => {
    document.title = "404 — Page Not Found | RADIECE PRIVATE LIMITED";
    const metaRobots = document.querySelector('meta[name="robots"]');
    if (metaRobots) {
      metaRobots.setAttribute("content", "noindex, follow");
    }
  }, []);

  const openSearch = () => {
    const trigger = document.getElementById("search-trigger");
    if (trigger) trigger.click();
  };

  const recommendedDestinations = [
    {
      title: "Engineering Services",
      desc: "IT & ITeS, SaaS (RaBuOS), Applied R&D hardware prototyping, and industrial robotics.",
      href: "/services",
      badge: "Capabilities",
      icon: "⚙️",
    },
    {
      title: "Technology Courses",
      desc: "Full-stack development, Python, Embedded Systems & IoT hardware training.",
      href: "/courses",
      badge: "Education",
      icon: "🎓",
    },
    {
      title: "Technical Publications",
      desc: "Deep architectural articles on Bun runtime, LoRaWAN wireless mesh, and DPDP compliance.",
      href: "/blog",
      badge: "Engineering Blog",
      icon: "📄",
    },
    {
      title: "Newsfeed & Dispatches",
      desc: "Official corporate announcements, RaBuOS development milestones, and hardware lab updates.",
      href: "/news",
      badge: "Announcements",
      icon: "📰",
    },
  ];

  return (
    <div className="min-h-[80vh] py-16 sm:py-24 px-4 sm:px-6 lg:px-8 flex items-center justify-center bg-slate-50 dark:bg-[#070b12] text-slate-900 dark:text-slate-100 transition-colors">
      <div className="max-w-4xl w-full">
        {/* Main Error Banner Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
          className="bg-white dark:bg-[#0d1624] border border-slate-200 dark:border-slate-800/80 rounded-3xl p-8 sm:p-12 shadow-xl shadow-slate-900/5 relative overflow-hidden text-center"
        >
          {/* Subtle Ambient Background Glow */}
          <div className="absolute -top-24 left-1/2 -translate-x-1/2 w-96 h-96 bg-primary/10 rounded-full blur-3xl pointer-events-none" />

          {/* Status Badge */}
          <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-amber-500/10 dark:bg-amber-400/10 border border-amber-500/20 text-amber-700 dark:text-amber-300 text-xs font-mono font-bold uppercase tracking-wider mb-6">
            <span className="w-2 h-2 rounded-full bg-amber-500 animate-pulse" />
            HTTP 404: RESOURCE_NOT_FOUND
          </div>

          {/* Large Hero 404 Code */}
          <h1 className="text-7xl sm:text-8xl lg:text-9xl font-black tracking-tighter text-brand-dark dark:text-white mb-4 select-none">
            <span className="bg-gradient-to-br from-brand via-sky-600 to-primary bg-clip-text text-transparent dark:from-sky-300 dark:via-primary dark:to-cyan-200">
              404
            </span>
          </h1>

          <h2 className="text-2xl sm:text-3xl font-extrabold text-brand-dark dark:text-slate-100 tracking-tight mb-4">
            Requested Endpoint Unresolved
          </h2>

          <p className="text-sm sm:text-base text-slate-600 dark:text-slate-300 font-light max-w-xl mx-auto leading-relaxed mb-6">
            The target path{" "}
            {currentPath && (
              <code className="px-2 py-0.5 rounded bg-slate-100 dark:bg-slate-800 text-brand dark:text-primary font-mono text-xs sm:text-sm font-semibold">
                {currentPath}
              </code>
            )}{" "}
            does not map to an active route on this server or may have been relocated.
          </p>

          {/* Action CTAs */}
          <div className="flex flex-wrap items-center justify-center gap-3.5 mb-10">
            <a
              href="/"
              className="btn-shine inline-flex items-center gap-2 bg-primary hover:bg-sky-400 text-slate-950 px-6 py-3 rounded-xl font-bold text-xs sm:text-sm uppercase tracking-wider shadow-md transition-transform hover:scale-[1.02] active:scale-[0.98]"
            >
              ← Return to Homepage
            </a>

            <button
              onClick={openSearch}
              className="inline-flex items-center gap-2 bg-slate-100 dark:bg-slate-800/80 hover:bg-slate-200 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-200 border border-slate-200 dark:border-slate-700 px-5 py-3 rounded-xl font-bold text-xs sm:text-sm uppercase tracking-wider transition-colors cursor-pointer"
            >
              <svg className="w-4 h-4 text-primary" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z" />
              </svg>
              Search Site (Ctrl+K)
            </button>

            <a
              href="/contact"
              className="inline-flex items-center gap-2 bg-transparent hover:bg-slate-100 dark:hover:bg-slate-800/50 text-slate-600 dark:text-slate-400 hover:text-brand dark:hover:text-white px-5 py-3 rounded-xl font-medium text-xs sm:text-sm transition-colors border border-transparent hover:border-slate-200 dark:hover:border-slate-700"
            >
              Contact Support Desk →
            </a>
          </div>

          {/* Section Divider */}
          <div className="pt-8 border-t border-slate-200/80 dark:border-slate-800/80 text-left">
            <h3 className="text-xs font-bold uppercase tracking-wider text-slate-400 dark:text-slate-500 mb-4 text-center sm:text-left">
              Explore Active System Sections
            </h3>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5">
              {recommendedDestinations.map((dest) => (
                <a
                  key={dest.href}
                  href={dest.href}
                  className="card-interactive block p-4 rounded-2xl bg-slate-50/70 dark:bg-[#080d16] border border-slate-200/80 dark:border-slate-800/80 hover:border-brand/40 dark:hover:border-primary/40 transition-colors group"
                >
                  <div className="flex items-center justify-between gap-2 mb-1.5">
                    <span className="text-xs font-bold text-brand-dark dark:text-slate-100 group-hover:text-brand dark:group-hover:text-primary transition-colors flex items-center gap-1.5">
                      <span>{dest.icon}</span>
                      <span>{dest.title}</span>
                    </span>
                    <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded bg-white dark:bg-slate-800 text-slate-500 dark:text-slate-400 border border-slate-200/60 dark:border-slate-700/60">
                      {dest.badge}
                    </span>
                  </div>
                  <p className="text-xs text-slate-600 dark:text-slate-400 font-light leading-relaxed">
                    {dest.desc}
                  </p>
                </a>
              ))}
            </div>
          </div>
        </motion.div>

        {/* Corporate Statutory Tagline */}
        <p className="text-center text-xs text-slate-400 dark:text-slate-600 mt-6 font-mono">
          RADIECE PRIVATE LIMITED • CIN: U58200KL2026PTC104862 • Kakkanad, Ernakulam
        </p>
      </div>
    </div>
  );
}
